// context/BreachesContext.tsx
import { ReactNode, createContext, useContext, useEffect, useState, useCallback } from "react";
import { UserContext } from "features/account";
import {
  getClientBreaches as apiGetClientBreaches,
  checkNewDataBreach as apiCheckNewDataBreach,
  getBreachesByEmail as apiGetBreachByEmail,
} from "../api";
import { ClientDataBreached, BreachStatus } from "../types/timeline.types";

const BreachesContext = createContext<{
  breaches: ClientDataBreached[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  getBreaches: () => Promise<void>;
  getBreachesByEmail: (email: string) => Promise<void>;
  checkNewBreaches: () => Promise<boolean>;
  markAsRead: (breachId: number) => void;
  markAllAsRead: () => void;
  updateBreachStatus: (breachId: number, status: BreachStatus) => Promise<any>;
  refreshBreaches: () => Promise<void>;
  clearError: () => void;
}>({
  breaches: [],
  unreadCount: 0,
  loading: false,
  error: null,
  getBreaches: async () => {},
  getBreachesByEmail: async () => {},
  checkNewBreaches: async () => false,
  markAsRead: () => {},
  markAllAsRead: () => {},
  updateBreachStatus: async () => {},
  refreshBreaches: async () => {},
  clearError: () => {},
});

const BreachesContextProvider = ({ children }: { children: ReactNode }) => {
  const [breaches, setBreaches] = useState<ClientDataBreached[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { userType } = useContext(UserContext);

  // Calculer le nombre de brèches non lues (statut 'new')
  const unreadCount = breaches.filter(b => b.status === 'new').length;

  const clearError = () => setError(null);

  /**
   * Récupérer toutes les brèches
   */
  const getBreaches = useCallback(async () => {
    if (!userType) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiGetClientBreaches(userType);

      // Adapter selon la structure de votre réponse
      if (response.data) {
        setBreaches(response.data);
      } else if (Array.isArray(response)) {
        setBreaches(response);
      }
    } catch (e: any) {
      console.error('Erreur getBreaches:', e);
      setError(e.message || 'Erreur lors de la récupération des brèches');
    } finally {
      setLoading(false);
    }
  }, [userType]);

  /**
   * Vérifier les nouvelles brèches
   * @returns boolean - true si de nouvelles brèches ont été détectées
   */
  const checkNewBreaches = async (): Promise<boolean> => {
    if (!userType) throw new Error('User type not defined');

    setLoading(true);
    setError(null);

    try {
      const response = await apiCheckNewDataBreach(userType);

      // Si de nouvelles brèches sont détectées, rafraîchir la liste
      if (response?.hasNewBreaches) {
        await getBreaches();
      }

      // Retourner true si de nouvelles brèches ont été trouvées
      return response?.hasNewBreaches || false;
    } catch (e: any) {
      console.error('Erreur checkNewBreaches:', e);
      setError(e.message || 'Erreur lors de la vérification des brèches');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Récupérer toutes les brèches
   */
  const getBreachesByEmail = useCallback(async (email: string) => {
    if (!email) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiGetBreachByEmail(email);

      // Adapter selon la structure de votre réponse
      if (response.data) {
        setBreaches(response.data);
      } else if (Array.isArray(response)) {
        setBreaches(response);
      }
    } catch (e: any) {
      console.error('Erreur getBreaches:', e);
      setError(e.message || 'Erreur lors de la récupération des brèches');
    } finally {
      setLoading(false);
    }
  }, [userType]);

  /**
   * Marquer une brèche comme lue (change le statut de 'new' à un autre statut)
   * Note: Dans les types, 'viewed' n'existe pas, on utilise 'in-progress' ou autre
   */
  const markAsRead = (breachId: number) => {
    setBreaches(prev =>
      prev.map(breach =>
        breach.id === breachId && breach.status === 'new'
          ? { ...breach, status: 'in-progress' } // ou un autre statut approprié
          : breach
      )
    );
  };

  /**
   * Marquer toutes les brèches comme lues
   */
  const markAllAsRead = () => {
    setBreaches(prev =>
      prev.map(breach =>
        breach.status === 'new' ? { ...breach, status: 'in-progress' } : breach
      )
    );
  };

  /**
   * Mettre à jour le statut d'une brèche
   */
  const updateBreachStatus = async (breachId: number, status: BreachStatus) => {
    if (!userType) throw new Error('User type not defined');

    setLoading(true);
    setError(null);

    try {
      // À implémenter selon votre API
      // const response = await apiUpdateBreachStatus(breachId, status, userType);

      // Mise à jour locale
      setBreaches(prev =>
        prev.map(breach =>
          breach.id === breachId
            ? { ...breach, status }
            : breach
        )
      );

      // return response;
    } catch (e: any) {
      console.error(`Erreur updateBreachStatus ${breachId}:`, e);
      setError(e.message || 'Erreur lors de la mise à jour du statut');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Rafraîchir toutes les brèches
   */
  const refreshBreaches = useCallback(async () => {
    await getBreaches();
  }, [getBreaches]);

  // Chargement initial
  useEffect(() => {
    if (userType === 'individual' || userType === 'partner') {
      getBreaches();
    }
  }, [userType, getBreaches]);

  const value = {
    breaches,
    unreadCount,
    loading,
    error,
    getBreaches,
    checkNewBreaches,
    getBreachesByEmail,
    markAsRead,
    markAllAsRead,
    updateBreachStatus,
    refreshBreaches,
    clearError
  };

  return (
    <BreachesContext.Provider value={value}>
      {children}
    </BreachesContext.Provider>
  );
};

export { BreachesContext, BreachesContextProvider };